/**
 * Checks if a JWT token is valid by verifying its structure and expiration.
 *
 * A valid JWT token is expected to have three parts separated by dots (header, payload, signature).
 * The payload is base64 decoded, and its 'exp' claim (expiration time) is checked
 * against the current time. The token is considered valid if the expiration time
 * is in the future.
 *
 * @param token The JWT token string to validate.
 * @returns True if the token is valid, false otherwise.
 */
const is_valid = (token: string): boolean => {
  if (token === '') return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    const payload = parse_payload(token);
    if (!payload) return false;

    // Ensure payload.exp exists and is a number before comparison
    if (typeof payload.exp !== 'number') return false;
    return payload.exp > Date.now() / 1000;
  } catch (e) {
    console.warn('api: unable to parse jwt token');
    return false;
  }
};

/**
 * Checks if a sliding token can be refreshed.
 * For sliding tokens, they can be refreshed as long as they haven't exceeded
 * their refresh lifetime, even if they're expired for access.
 *
 * @param token The sliding token string to validate.
 * @returns True if the token can be refreshed, false otherwise.
 */
const can_refresh = (token: string): boolean => {
  if (token === '') return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    const payload = parse_payload(token);
    if (!payload) return false;

    // For sliding tokens, check the refresh_exp claim if available
    // If not available, fall back to regular exp check
    const refresh_exp = payload.refresh_exp || payload.exp;

    if (typeof refresh_exp !== 'number') return false;
    return refresh_exp > Date.now() / 1000;
  } catch (e) {
    console.warn('api: unable to parse sliding token for refresh check');
    return false;
  }
};

/**
 * Checks if a token is a sliding token by examining its token_type claim.
 *
 * @param token The JWT token string to check.
 * @returns True if the token is a sliding token, false otherwise.
 */
const is_sliding_token = (token: string): boolean => {
  if (token === '') return false;

  try {
    const payload = parse_payload(token);
    if (!payload) return false;

    // Check if token_type indicates this is a sliding token
    return payload.token_type === 'sliding';
  } catch (e) {
    console.warn('api: unable to parse token type');
    return false;
  }
};

/**
 * Parses the payload from a JWT token.
 *
 * @param token The JWT token string to parse.
 * @returns The parsed payload object or null if parsing fails.
 */
const parse_payload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (e) {
    return null;
  }
};

/**
 * Gets the expiration time of a token in seconds since epoch.
 *
 * @param token The JWT token string.
 * @returns The expiration time in seconds, or null if not available.
 */
const get_expiration = (token: string): number | null => {
  try {
    const payload = parse_payload(token);
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp;
  } catch (e) {
    return null;
  }
};

/**
 * Gets the refresh expiration time of a sliding token in seconds since epoch.
 *
 * @param token The sliding token string.
 * @returns The refresh expiration time in seconds, or null if not available.
 */
const get_refresh_expiration = (token: string): number | null => {
  try {
    const payload = parse_payload(token);
    if (!payload) return null;

    // Try refresh_exp first, fall back to exp
    const refresh_exp = payload.refresh_exp || payload.exp;
    if (typeof refresh_exp !== 'number') return null;

    return refresh_exp;
  } catch (e) {
    return null;
  }
};

/**
 * Gets the user ID from a token.
 *
 * @param token The JWT token string.
 * @returns The user ID, or null if not available.
 */
const get_user_id = (token: string): string | null => {
  try {
    const payload = parse_payload(token);
    if (!payload) return null;

    // Check both user_id and sub claims
    const user_id = payload.user_id || payload.sub;
    return typeof user_id === 'string' ? user_id : null;
  } catch (e) {
    return null;
  }
};

/**
 * Checks if a token should be refreshed proactively.
 *
 * A token should be refreshed if it's nearing its expiration and is still
 * within its refresh lifetime.
 *
 * @param token The JWT token string.
 * @param margin The buffer in seconds before expiration to trigger a refresh.
 * @returns True if the token should be refreshed, false otherwise.
 */
const should_refresh = (token: string, margin: number = 60): boolean => {
  const expiration = get_expiration(token);
  if (expiration === null) {
    return false;
  }

  const now = Date.now() / 1000;
  const is_nearing_expiry = expiration - now < margin;

  return is_nearing_expiry && can_refresh(token);
};

export const jwt = {
  is_valid,
  can_refresh,
  is_sliding_token,
  parse_payload,
  get_expiration,
  get_refresh_expiration,
  get_user_id,
  should_refresh,
};
